import { Icon } from '@sdb/planning-ui';
import { Tooltip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import type {
  HoursPayOutRequestResponse,
  HoursPayOutRequestStatus,
  HoursPayOutRequestType
} from 'generated/index';
import { formatMinutesInHours } from 'utils/date';
import { dateFormatter } from 'utils/grid';

const formatMinutesOrBlank = (value: number | null | undefined) =>
  value === null || value === undefined
    ? ''
    : formatMinutesInHours(value, true);

const useColumnDefinitions = () => {
  'use memo';

  const { t } = useTranslation('common');

  return [
    {
      field: 'employmentNumber',
      type: 'string',
      headerName: t('hoursPayOutRequestsOverview.employmentNumber'),
      valueGetter: (_, row: HoursPayOutRequestResponse) =>
        row?.employment?.number ?? '',
      minWidth: 170
    },
    {
      field: 'employeeNumber',
      type: 'string',
      headerName: t('hoursPayOutRequestsOverview.employeeNumber'),
      valueGetter: (_, row: HoursPayOutRequestResponse) =>
        row?.employment?.employeeNumber ?? '',
      minWidth: 170
    },
    {
      field: 'employeeName',
      type: 'string',
      headerName: t('employee'),
      valueGetter: (_, row: HoursPayOutRequestResponse) =>
        row?.employment?.employeeName ?? '',
      minWidth: 220,
      flex: 1
    },
    {
      field: 'requestDate',
      type: 'date',
      headerName: t('hoursPayOutRequestsOverview.requestDate'),
      valueFormatter: dateFormatter,
      width: 150
    },
    {
      field: 'type',
      type: 'string',
      headerName: t('type'),
      valueFormatter: (value: HoursPayOutRequestType) =>
        value ? t(`hoursPayOutRequestsOverview.type.${value}`) : '',
      minWidth: 150
    },
    {
      field: 'amountInMinutes',
      type: 'string',
      headerName: t('hoursPayOutRequestsOverview.amount'),
      valueFormatter: (value: number) => formatMinutesInHours(value, true),
      width: 140
    },
    {
      field: 'processedAmountInMinutes',
      type: 'string',
      headerName: t('hoursPayOutRequestsOverview.processedAmount'),
      description: t('hoursPayOutRequestsOverview.processedAmountDescription'),
      valueFormatter: (value: number | null) => formatMinutesOrBlank(value),
      renderCell: ({ value, row }) => {
        const mismatch =
          row.status === 'APPROVED' && value !== row.amountInMinutes;
        return (
          <Typography variant="body2" color={mismatch ? 'error' : undefined}>
            {formatMinutesOrBlank(value)}
          </Typography>
        );
      },
      width: 140
    },
    {
      field: 'processedDate',
      type: 'date',
      headerName: t('hoursPayOutRequestsOverview.processedDate'),
      description: t('hoursPayOutRequestsOverview.processedDateDescription'),
      valueFormatter: dateFormatter,
      width: 150
    },
    {
      field: 'balanceAtRequestInMinutes',
      type: 'string',
      headerName: t('hoursPayOutRequestsOverview.balanceAtRequest'),
      valueFormatter: (value: number | null) => formatMinutesOrBlank(value),
      width: 160
    },
    {
      field: 'remainingInMinutes',
      type: 'string',
      headerName: t('hoursPayOutRequestsOverview.currentBalance'),
      valueFormatter: (value: number | null) => formatMinutesOrBlank(value),
      width: 140
    },
    {
      field: 'status',
      type: 'string',
      headerName: t('status'),
      valueFormatter: (value: HoursPayOutRequestStatus) =>
        value ? t(`hoursPayOutRequestsOverview.status.${value}`) : '',
      width: 140
    },
    {
      field: 'statusNote',
      headerName: t('hoursPayOutRequestsOverview.rejectReason'),
      width: 130,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: ({ row }) =>
        row.status === 'REJECTED' && row.statusNote ? (
          <Tooltip title={row.statusNote}>
            <Icon type="note" color="action" fontSize="small" />
          </Tooltip>
        ) : null
    }
  ] as GridColDef[];
};

export default useColumnDefinitions;
